import { Stack, Typography } from "@mui/material";
import {
  OrderOut,
  OrderIn,
  PatientIn,
  useWebApiV1UpdateOrderMutation,
  useWebApiV1UpdatePatientMutation,
  PatientOut,
  useProtocolApiV1ListDataQuery,
  useWebApiV1GetTenantPhysiciansQuery,
} from "@providers/hop-ord-server/api";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, UseFormReturn, useForm } from "react-hook-form";
import {
  FormContainer,
  TextFieldElement,
  SelectElement,
} from "react-hook-form-mui";
import { debounce } from "lodash";
import { toServerDateString } from "@utils";
import dayjs, { Dayjs } from "dayjs";
import { ListDataCategory } from "@enums";
import { DatePicker } from "@mui/x-date-pickers";
import { FIELD_REQUIRED_MESSAGE } from "../../constants";

export interface InputTypes {
  patient: {
    firstName: string | null;
    lastName: string | null;
    dateOfBirth?: Dayjs;
    medicalReferenceNumber: string | null;
  };
  order: {
    treatingDepartment: string;
    treatingRoId: number | null;
  };
}

interface Props {
  patientId: number;
  orderId: number;
  orderData: OrderOut;
  patientData?: PatientOut;
  isReadOnly: boolean;
  isImported?: boolean;
  setForm?: (form: UseFormReturn<InputTypes> | null) => void;
}

const PatientDetailsForm = ({
  patientId,
  orderId,
  orderData,
  patientData,
  isReadOnly,
  isImported = false,
  setForm,
}: Props) => {
  const [updatePatient] = useWebApiV1UpdatePatientMutation();
  const [updateOrder] = useWebApiV1UpdateOrderMutation();

  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: [orderData?.tenantId],
  });

  const { data: physicians } = useWebApiV1GetTenantPhysiciansQuery({
    tenantId: orderData?.tenantId,
  });

  const form = useForm<InputTypes>({
    defaultValues: {
      order: {
        treatingDepartment: orderData?.treatingDepartment || undefined,
        treatingRoId: orderData?.treatingRoId || null,
      },
      patient: {
        ...patientData,
        dateOfBirth: patientData?.dateOfBirth
          ? dayjs(patientData?.dateOfBirth)
          : undefined,
      },
    },
    mode: "all",
  });

  useEffect(() => {
    if (!setForm) return;
    setForm(form);
  }, [setForm, form]);

  // create some debounced updaters
  // react isn't happy if you provide the debounce fn as the callback
  // so we momoise the result of `debounce` and use it for callbacks later
  const debouncedUpdatePatient = useMemo(
    () =>
      debounce((v) => {
        updatePatient({
          patientId,
          patientIn: { ...v },
        }).then();
      }, 1000),
    [patientId, updatePatient],
  );

  const debouncedUpdateOrder = useMemo(
    () =>
      debounce((v) => {
        updateOrder({
          orderId,
          orderIn: v,
        }).then();
      }, 1000),
    [orderId, updateOrder],
  );

  // create callbacks using the memoised change handlers
  const handlePatientChange = useCallback(
    (v: PatientIn) => debouncedUpdatePatient(v),
    [debouncedUpdatePatient],
  );

  const handleOrderChange = useCallback(
    (v: OrderIn) => debouncedUpdateOrder(v),
    [debouncedUpdateOrder],
  );

  const watch = form.watch;

  // setup the watch, making sure to unsub it once the cmpt dismounts
  useEffect(() => {
    const { unsubscribe } = watch((v, { name }) => {
      const groupName = name?.split(".")[0];
      switch (groupName) {
        case "patient":
          {
            const dateOfBirth = toServerDateString(
              v.patient?.dateOfBirth as Dayjs,
            );
            handlePatientChange({
              ...v.patient,
              dateOfBirth,
            });
          }
          break;
        case "order":
          handleOrderChange(v.order ?? {});
          break;
      }
    });
    return () => unsubscribe();
  }, [handlePatientChange, handleOrderChange, watch]);

  return (
    <FormContainer formContext={form}>
      <Stack spacing={2}>
        <Typography
          variant="h6"
          id="treating-clinic"
          sx={{ scrollMarginTop: "18rem" }}
        >
          Treating Clinic
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <SelectElement
            fullWidth
            label="Treating clinic"
            name="order.treatingDepartment"
            required
            disabled={isReadOnly}
            data-testid="form-treating-clinic"
            options={
              treatingDepartments?.map((dept) => ({
                id: dept.value,
                label: dept.value,
              })) || []
            }
          />
          <SelectElement
            fullWidth
            label="Requested physician"
            name="order.treatingRoId"
            required
            disabled={isReadOnly && !isImported}
            data-testid="form-requested-physician"
            options={
              physicians?.map((physician) => ({
                id: physician.id,
                label: physician.name,
              })) || []
            }
          />
        </Stack>
        <Stack>
          <Typography variant="h6" id="patient-details">
            Patient Details
          </Typography>
          <Typography
            variant="body2"
            sx={{
              paddingBottom: 2,
              scrollMarginTop: "18rem",
            }}
          >
            Please ensure the following patient details match exactly with the
            EMR to avoid potential integration issues.
          </Typography>
          <Stack direction="column" spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextFieldElement
                fullWidth
                label="First name"
                name="patient.firstName"
                required
                disabled={isReadOnly}
                data-testid="form-first-name"
              />
              <TextFieldElement
                fullWidth
                label="Last name"
                name="patient.lastName"
                required
                disabled={isReadOnly}
                data-testid="form-last-name"
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                control={form.control}
                name="patient.dateOfBirth"
                rules={{ required: FIELD_REQUIRED_MESSAGE }}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Date of birth"
                    name={field.name}
                    onChange={field.onChange}
                    data-testid="form-dob"
                    value={field.value}
                    desktopModeMediaQuery="(min-width:600px)"
                    disabled={isReadOnly}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                        onBlur: field.onBlur,
                      },
                      field: {
                        clearable: true,
                      },
                    }}
                  />
                )}
              />
              <TextFieldElement
                fullWidth
                label="Patient ID"
                name="patient.medicalReferenceNumber"
                required
                disabled={isReadOnly}
                data-testid="form-patient-id"
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </FormContainer>
  );
};

export default PatientDetailsForm;
